import { t as tr } from './i18n';
import { fmtNum, lei } from './format';
import { money } from './package';
import type { AdditionKind, Offer, OfferAddition, OfferAdvance } from './types';

/**
 * Condițiile comerciale ale unei oferte, ținute ca date, nu doar ca text:
 * avansul, termenul de plată, termenul de livrare, valabilitatea. Textul din
 * ofertă rămâne alături, ca dovadă; numerele sunt ce se poate compara.
 *
 * Și pozițiile puse în plus de furnizor (console, decofrol, montaj): se văd, se
 * adună separat, dar nu intră în costul total comparat.
 */

/** Avansul pe scurt: „fără avans”, „30%”, „5.000 lei”, sau textul ofertei. */
export function advanceText(a: OfferAdvance | null | undefined, offer?: Pick<Offer, 'currency'>): string {
  if (!a) return '';
  if (a.kind === 'none') return tr('fără avans');
  if (a.kind === 'percent' && a.value != null) return tr('{0}% avans', fmtNum(a.value));
  if (a.kind === 'fixed' && a.value != null) return `${money(a.value, offer?.currency)} avans`;
  return a.text;
}

/** „30 de zile” cu textul ofertei alături, dacă spune mai mult. */
export function daysWithText(days: number | null | undefined, text: string): string {
  const d =
    days == null ? '' : days === 0 ? 'imediat' : `${fmtNum(days)} ${days === 1 ? tr('zi') : tr('zile')}`;
  if (!d) return text;
  if (!text || text.replace(/\s+/g, ' ').trim() === d) return d;
  return `${d} · ${text}`;
}

/**
 * Oferta a expirat: ziua de azi e după `validUntil`. Prețurile unei oferte
 * expirate pot să nu mai țină; omul trebuie să afle asta înainte să comande.
 */
export const offerExpired = (offer: Pick<Offer, 'validUntil'>, today: string): boolean =>
  !!offer.validUntil && offer.validUntil < today;

const KIND_LABEL: Record<AdditionKind, string> = {
  accessory: 'accesoriu',
  consumable: 'consumabil',
  service: 'serviciu',
  other: 'altele',
};

export const additionKindLabel = (k: AdditionKind): string => KIND_LABEL[k];

/** Valoarea unei poziții suplimentare, în moneda ofertei: cea scrisă, altfel cantitate × preț. */
export function additionAmount(a: OfferAddition): number | null {
  if (a.amount != null) return a.amount;
  if (a.qty != null && a.unitPrice != null) return Math.round(a.qty * a.unitPrice * 100) / 100;
  return null;
}

/**
 * Pozițiile suplimentare ale unei oferte, adunate în lei. `unknown` = câte n-au
 * sumă: totalul e doar o limită de jos.
 */
export function additionsTotal(offer: Offer): { lei: number; unknown: number; count: number } {
  const list = offer.additions ?? [];
  const fx = offer.currency === 'EUR' ? (offer.fxRate ?? 1) : 1;
  let sum = 0;
  let unknown = 0;
  for (const a of list) {
    const v = additionAmount(a);
    if (v == null) unknown += 1;
    else sum += v;
  }
  return { lei: sum * fx, unknown, count: list.length };
}

/** O poziție suplimentară pe un rând: „20 buc console de lucru × 50 lei = 1.000 lei”. */
export function additionLine(a: OfferAddition, offer: Pick<Offer, 'currency' | 'fxRate'>): string {
  const fx = offer.currency === 'EUR' ? (offer.fxRate ?? 1) : 1;
  const qty = a.qty != null ? `${fmtNum(a.qty)} ${a.unit} ` : '';
  const price = a.unitPrice != null ? ` × ${money(a.unitPrice, offer.currency)}` : '';
  const v = additionAmount(a);
  const total = v != null ? ` = ${lei(v * fx)}` : '';
  return `${qty}${a.desc}${price}${total}`;
}
